/**
 * In the water (docs/handoff/newdir/QUEUE.md 3d, data/qinggong.ts `WATER`): wading and swimming
 * (the mode is set by `tickQinggong`, qinggong.ts), diving under, breath (ลมหายใจ) and drowning,
 * and the currents and waves that push a swimmer. The World's qinggong tick calls `tickWater` for
 * the player and applies what it returns (the push through its own movement, the health lost and a
 * drowning's death); this system keeps weapons, skills and jumps away from a swimmer.
 * Deterministic: no randomness; waves are a function of the World's game time.
 */
import { WATER } from '../data/qinggong';
import { STORM, stormAt, WORLD_EVENTS_ZONE } from '../data/worldEvents';
import { waterSurfaceAt, type GameMap } from '../map';
import { isWaterAt, swimming, waterDepthAt, type QinggongState } from '../qinggong';
import type { PlayerCommand } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** The sea's still surface (metres); water above it is a river or a lake. */
const SEA_TOP = 0.05;

/** What the water does at a point and time: the push (metres a second) and the wave's height (metres). */
export interface WaterFlow {
  x: number;
  y: number;
  wave: number;
}

/**
 * The current at (x, y) at game time `t`. A river (a region's water above the sea) pushes downhill
 * along its surface. The sea (an authored map's sea, a region's sea level) swells toward the
 * shallows, with a steady drift and a surge on each wave's crest. Still water: no push.
 */
export function currentAt(m: GameMap, x: number, y: number, t: number): WaterFlow {
  const none = { x: 0, y: 0, wave: 0 };
  if (!isWaterAt(m, x, y)) return none;
  const surface = (sx: number, sy: number, d: number) => (isWaterAt(m, sx, sy) ? waterSurfaceAt(m, sx, sy) : d);
  const here = waterSurfaceAt(m, x, y);
  if (m.chunks && here > SEA_TOP) {
    // A river: down the slope of its surface (a bank counts as level with the water here).
    const gx = (surface(x + 1, y, here) - surface(x - 1, y, here)) / 2;
    const gy = (surface(x, y + 1, here) - surface(x, y - 1, here)) / 2;
    const g = Math.hypot(gx, gy);
    if (g <= 0) return none;
    const push = Math.min(WATER.river.max, g * WATER.river.push);
    return { x: (-gx / g) * push, y: (-gy / g) * push, wave: 0 };
  }
  // The sea: toward where it gets shallower (the shore counts as no depth).
  const gx = (waterDepthAt(m, x + 2, y) - waterDepthAt(m, x - 2, y)) / 4;
  const gy = (waterDepthAt(m, x, y + 2) - waterDepthAt(m, x, y - 2)) / 4;
  const g = Math.hypot(gx, gy);
  if (g <= 0) return none;
  const [ux, uy] = [-gx / g, -gy / g];
  const S = WATER.sea;
  const phase = 2 * Math.PI * (t / S.period - (x * ux + y * uy) / S.length);
  const crest = Math.sin(phase);
  // The storm at sea (data/worldEvents.ts): the surge doubles for the event.
  const surge = m.zone === WORLD_EVENTS_ZONE && stormAt(t) ? S.surge * STORM.surgeMul : S.surge;
  const push = S.drift + surge * Math.max(0, crest);
  return { x: ux * push, y: uy * push, wave: S.amp * crest };
}

/** What a tick in the water came to, for the World to apply. */
export interface WaterTick {
  /** The water's push this tick (metres a second). */
  pushX: number;
  pushY: number;
  /** Health lost to drowning this tick. */
  hp: number;
  /** A line for the player (out of breath, back up). */
  note: string | null;
}

/**
 * One tick of the player in the water (after `tickQinggong` has set wade or swim): diving while
 * `dive` is held (the flying mount's down key, `fly` −1) and floating back up when let go, stamina
 * for swimming (`moving`), treading or diving, breath drained under the water or with no stamina
 * left in deep water, and given back otherwise; out of breath, health drains. Out of the water the
 * breath comes back and the current and wave are cleared.
 */
export function tickWater(
  p: { x: number; y: number; stamina: number; maxHp: number },
  st: QinggongState,
  m: GameMap,
  t: number,
  dive: boolean,
  moving: boolean,
  dt: number,
): WaterTick {
  const out: WaterTick = { pushX: 0, pushY: 0, hp: 0, note: null };
  if (!swimming(st)) {
    st.dive = 0;
    st.currentX = st.currentY = st.wave = 0;
    st.breath = Math.min(WATER.breath, st.breath + WATER.refill * dt);
    return out;
  }
  // Diving: down while held, as far as the bed allows; back up when let go.
  const floor = Math.max(0, st.depth - WATER.dive.bed);
  st.dive = dive
    ? Math.min(floor, st.dive + WATER.dive.down * dt)
    : Math.max(0, st.dive - WATER.dive.up * dt);
  st.mode = st.dive > 0 ? 'dive' : 'swim';
  const cost = st.mode === 'dive' ? WATER.stamina.dive : moving ? WATER.stamina.swim : WATER.stamina.tread;
  p.stamina = Math.max(0, p.stamina - cost * dt);
  // Breath: held under the water, and gone when there is no stamina left to keep the head up.
  const was = st.breath;
  if (st.mode === 'dive' || p.stamina <= 0) st.breath = Math.max(0, st.breath - dt);
  else st.breath = Math.min(WATER.breath, st.breath + WATER.refill * dt);
  if (st.breath <= 0) {
    out.hp = p.maxHp * WATER.drown * dt;
    if (was > 0) out.note = 'หมดลมหายใจ! รีบขึ้นฝั่ง';
  }
  // The water's push: a diver deep under feels a third of the sea's.
  const f = currentAt(m, p.x, p.y, t);
  const k = st.dive > WATER.sea.calm ? 1 / 3 : 1;
  st.currentX = f.x * k;
  st.currentY = f.y * k;
  st.wave = f.wave;
  out.pushX = st.currentX;
  out.pushY = st.currentY;
  return out;
}

/**
 * Whether a monster may step into the water at (x, y): only in a fight or on its way home after one (never while
 * idling about), and only where it is shallow enough to wade (`WATER.swimDepth`). Deep water stays safe from them.
 */
export function wadesAt(
  m: GameMap,
  e: { kind: string; brain?: { state: string } | null },
  x: number,
  y: number,
): boolean {
  if (e.kind !== 'monster' || !e.brain || e.brain.state === 'idle' || !isWaterAt(m, x, y)) return false;
  return waterDepthAt(m, x, y) < WATER.swimDepth;
}

/** Commands a swimmer cannot give: no weapons, no skills, no jumps out of deep water. */
const DRY_ONLY: ReadonlySet<PlayerCommand['t']> = new Set<PlayerCommand['t']>([
  'attack',
  'skill',
  'throwHidden',
  'engage',
  'block',
  'dash',
  'jump',
  'meditate',
  'mount',
]);
/** Of those, the ones answered with a line (the rest are swallowed quietly). */
const TOLD: ReadonlySet<PlayerCommand['t']> = new Set<PlayerCommand['t']>(['attack', 'skill', 'throwHidden']);

export class WaterSystem implements WorldSystem {
  readonly name = 'water';

  constructor(private readonly h: SystemHost) {}

  refuse(cmd: PlayerCommand): boolean {
    if (!swimming(this.h.world.qinggong) || !DRY_ONLY.has(cmd.t)) return false;
    if (TOLD.has(cmd.t)) this.h.notice('อยู่ในน้ำ ใช้อาวุธและวิชาไม่ได้');
    return true;
  }

  /** A swimmer lets go of a guard or a fight held from before going in. */
  playerStep(): void {
    if (!swimming(this.h.world.qinggong)) return;
    this.h.input.block = false;
    this.h.input.engage = false;
  }
}
